/**
 * THE EXAM EDITOR — the real compiled page, not a mock of it. The bottom layer is the last compile's
 * rich-content SVG, fetched on demand from `getExamSvgQuery` (NOT persisted on `Exam` — once a figure is
 * involved the SVG embeds it as base64, and storing that alongside tasks/additionalInformation risks
 * pushing the whole document past what one write can carry, silently losing everything else in it; a
 * recompile-only-for-the-preview costs about ten milliseconds instead). A transparent overlay sits exactly
 * on top of it, one invisible hitbox per task/material, positioned from `Exam.examLayoutJson` (small — just
 * numbers — so it stays persisted; every block's on-page extent in points, see `computeBoundingBoxes` in
 * exam-structure.ts). Hovering a hitbox outlines it; clicking one opens a floating form anchored to it
 * (@floating-ui/react) for editing/deleting that block.
 *
 * HITBOXES NEST, because typst's geometry does: a `#task` wrapping subtasks (or an `#mc`, or a
 * `#figure-block`) directly inside its own body gets its own start/end position pair the same way a
 * top-level task does (see exam.typ's `gradias-mark`/`gradias-end`), so `Hitbox` recurses — it re-encodes
 * its OWN body with `NESTED_TASK_CALLS` and renders a smaller, higher-z-index hitbox (via DOM order, not an
 * explicit z-index) for every subtask/mc/figure it finds, each capable of the same recursion again. Nothing
 * about that recursion needs an index into the top-level store: every level is handed already-bound
 * `onPatchArgs`/`onPatchBody`/`onDelete` closures that know how to splice themselves back into their own
 * parent, so a subtask three levels deep writes through the exact same three splices whether it moves a
 * point value or gets deleted outright.
 *
 * A TASK'S OWN HITBOX IS JUST ITS HEADER, not its full start-to-end span: the label and body text share
 * one paragraph in exam.typ (no separate mark exists for "end of header"), but every subtask/mc/figure
 * inside already has its own real box, so `Hitbox` bounds its header at whichever nested child starts
 * first — no new typst geometry needed. This also keeps the header's hover/click target from ever
 * overlapping nested content's own boxes.
 *
 * A LABEL_IMAGE TASK'S CALLOUT DOTS ARE DRAGGABLE ON HOVER, no click required — positioned against
 * `${figureId}-image`, a box exam-structure.ts derives specifically for the rendered image (narrower and
 * shorter than the whole figure-block once callout number-boxes add gutters and a caption follows — see
 * exam.typ's `figure-image` mark), not the figure-block's own box, so a dragged dot lines up with the
 * actual picture. Visibility is plain CSS `:hover` on that same box, not React state fed by the
 * enclosing task's hover: after the header-only hitbox above stopped overlapping the image, a JS
 * mouseenter/leave pair split across those two sibling elements would fight itself the moment the
 * pointer reached the dots.
 *
 * MULTIPLE CHOICE HAS ITS OWN FORM, not a textarea: `#mc`'s options are a positional tuple of content
 * blocks (`( [A], [B] )`), addressed via `getPositionalArg`/`parseMcOptions` since exam-document.ts's
 * `getArg`/`setArg` only cover named arguments — McForm manages a plain array of strings and stitches it
 * back into `#mc(...)` syntax only at save time, so the brackets never reach the teacher.
 *
 * EVERY EDIT GOES THROUGH exam-document.ts: a popover save/delete or a dot drag all resolve to a
 * setArg/setBody/splice on the byte-exact typst-source store, decoded back to source and recompiled.
 */

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { logger } from '@adornis/base/logging.js';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  decodeExamDocument,
  encodeExamDocument,
  formatCallouts,
  formatMcOptions,
  formatTypstString,
  formatTypstStringArray,
  getArg,
  getBody,
  getOwnText,
  getPositionalArg,
  NESTED_TASK_CALLS,
  parseCallouts,
  parseMcOptions,
  parseTypstString,
  parseTypstStringArray,
  setArg,
  setBody,
  setOwnText,
  setPositionalArg,
  type ExamCallout,
  type ExamDocument,
  type ExamDocumentCallNode,
} from '../../../../../db/api/exam-creation/exam-document.js';
import {
  compileExamMutation,
  getExamSvgQuery,
  requestExamRefinementMutation,
  type ExamEditorSaveResult,
  type ExamSvgResult,
} from '../../../../../db/api/exam-creation/exam-editor-api.js';
import type { BlockBoundingBox, ExamLayout } from '../../../../../db/api/exam-creation/exam-structure.js';
import { getFileByIDQuery } from '../../../../../db/api/get-all-files.js';
import { getExamStateByExamIdQuery } from '../../../../../db/api/get-exams.js';
import { getTaskTypeLabel, TaskType } from '../../../../../db/api/task-types/task-main.js';
import type { Exam } from '../../../../../db/exam.js';

export interface ExamEditorProps {
  exam: Exam | null;
  /**
   * Locked once a master solution or grading scheme has been uploaded/generated, or student submissions
   * have arrived — see `isExamCreatorLocked` in db/utils/exam-lock.ts, the ONE definition chat.tsx derives
   * this from and the server enforces identically (exam-creation.job.ts's `ready()`, create-exam.tool.ts,
   * exam-editor-api.ts's mutations). No hitbox opens a popover, no "+" gap renders, and no AI-instruction
   * box is shown — the compiled preview underneath already IS the read view, so there is nothing an
   * un-editable popover would add. `compileExamMutation`/`requestExamRefinementMutation` refuse server-side
   * too, so this prop is a UX nicety on top of a real gate, not the gate itself.
   */
  isReadOnly?: boolean;
  /**
   * The session's account kind — used ONLY to show the "Online ausfüllen" button (opens
   * /student-exam/:examId) for a student and hide it for a teacher. This canvas is the SAME `examcreator`
   * view for both (see chat.tsx's `renderCanvasView`, which opens it unconditionally on `create_exam`,
   * unlike neighboring cases that already branch on role) — a teacher creates a class exam here, a student
   * their own self-practice one, and the button is that student's only way to reach the online fill-in
   * page (student-exam.ts's `resolveIsCurrentSessionStudent` now hard-blocks a teacher from that page
   * server-side, so showing it to a teacher would only ever lead to a "students only" notice).
   */
  role?: 'teacher' | 'student';
}

interface FigureAsset {
  materialId: string;
  fileId: string;
}

/** Bound by whoever renders a node to however that node writes back — a top-level index, or a splice into a parent's own nested body. Same shape at every recursion depth. */
interface NodeWriteBack {
  onPatchArgs: (patch: Record<string, string | null>) => void;
  /** `#mc`'s options tuple is the one thing addressed by position, not by a `key:` name — see McForm. */
  onPatchPositional: (rawValue: string) => void;
  onPatchBody: (body: string) => void;
  /**
   * Changes a task's `type` ARG and its BODY together, atomically — needed wherever a type change must also
   * rewrite the body in the same stroke (see `Hitbox`'s `handleTypeChange`: switching away from Multiple
   * Choice folds its `#mc` options into the body's own text and drops the node; switching to it inserts an
   * empty one). Two separate `onPatchArgs`/`onPatchBody` calls cannot do this safely for a NESTED task: both
   * recompute their payload from the same pre-edit `nested` snapshot closed over by `childWriteBack`, so the
   * second dispatch would silently discard the first's change instead of building on it.
   */
  onPatchTypeAndBody: (rawType: string, body: string) => void;
  onDelete: () => void;
}

function parseLayout(json: string | null | undefined): ExamLayout | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as ExamLayout;
  } catch {
    return null;
  }
}

function boxStyle(box: BlockBoundingBox, pageWidthPt: number, pageHeightPt: number, pageCount: number) {
  const totalHeightPt = pageHeightPt * pageCount;
  const pageIndex = Math.max(0, (box.page || 1) - 1); // Fallback to page 1 if missing

  return {
    left: `${(box.x / pageWidthPt) * 100}%`,
    top: `${((pageIndex * pageHeightPt + box.y) / totalHeightPt) * 100}%`,
    width: `${(box.width / pageWidthPt) * 100}%`,
    height: `${(box.height / totalHeightPt) * 100}%`,
  };
}

/**
 * How a block introduces itself to the refinement job — the same "what am I" a teacher would say pointing
 * at the page, so the composed instruction reads like a sentence a person wrote rather than an id dump.
 */
function describeBlockForAi(id: string, node: ExamDocumentCallNode): string {
  if (node.name === 'task') {
    const number = parseTypstString(getArg(node, 'number') ?? '');
    const taskType = parseTypstString(getArg(node, 'type') ?? '');
    const label = getTaskTypeLabel(taskType as TaskType) || taskType;
    return `task ${number || id}${label ? ` (${label})` : ''}`;
  }
  if (node.name === 'material') {
    const title = parseTypstString(getArg(node, 'title') ?? '');
    return title ? `the material "${title}"` : `the shared material ${id}`;
  }
  if (node.name === 'figure-block') return `the figure ${id}`;
  if (node.name === 'exam.with' || node.name === 'exam') return 'the exam header';
  return `the block ${id}`;
}

/** Splices `updated` in at `index` (or drops it, if `updated` is null) and hands the whole array to `onChange`. */
function spliceNode(document: ExamDocument, index: number, updated: ExamDocumentCallNode | null): ExamDocument {
  const next = document.slice();
  if (updated) next[index] = updated;
  else next.splice(index, 1);
  return next;
}

// eslint-disable-next-line require-unicode-regexp -- matches project convention elsewhere in exam-document.ts
const LETTERED_OPTION_LINE = /^([A-Z])\) (.*)$/;

/**
 * The inverse of `handleTypeChange`'s own MC→other-type conversion (see `Hitbox`): that path folds a task's
 * `#mc` options into its own text as a trailing "A) …\nB) …\n…" block. Retyping the SAME task back to
 * Multiple Choice should restore those as real options again rather than leaving them as inert prose next
 * to a fresh, empty `#mc` — this looks for exactly that shape at the END of the text and, if found, returns
 * the options plus whatever text came before them; `null` if the text doesn't end that way (an ordinary
 * task that was never Multiple Choice, most of the time).
 *
 * Deliberately narrow — sequential letters STARTING AT "A", one per line, only at the very end, at least two
 * of them — rather than "does this look like a list", so it only ever recognizes this file's own round-trip
 * format instead of mistaking a task's genuine prose (which might itself end in "...see options A and B")
 * for former options.
 */
function extractTrailingLetteredOptions(text: string): { remainingText: string; options: string[] } | null {
  const lines = text.split('\n');
  let start = lines.length;
  while (start > 0 && LETTERED_OPTION_LINE.test(lines[start - 1] ?? '')) start -= 1;

  const block = lines.slice(start);
  if (block.length < 2) return null;

  const matches = block.map(line => LETTERED_OPTION_LINE.exec(line)!);
  const isSequentialFromA = matches.every((match, index) => match[1] === String.fromCharCode(65 + index));
  if (!isSequentialFromA) return null;

  return {
    remainingText: lines.slice(0, start).join('\n').replace(/\n+$/u, ''),
    options: matches.map(match => match[2]!),
  };
}

export function ExamEditor({ exam, isReadOnly = false, role }: ExamEditorProps) {
  const { t } = useTranslation();
  const examId = (exam as any)?._id as string | undefined;
  const examSource = (exam as any)?.examSource as string | undefined;
  const figureAssets: FigureAsset[] = Array.isArray((exam as any)?.figureAssets) ? (exam as any).figureAssets : [];

  const [store, setStore] = useState<ExamDocument | null>(null);
  const [baseline, setBaseline] = useState('');
  const [svg, setSvg] = useState('');
  const [layout, setLayout] = useState<ExamLayout | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [result, setResult] = useState<ExamEditorSaveResult | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  /** Where popovers portal to — a node inside this view's own DOM subtree, not `document.body`, so a
   * flip()/shift()-relocated popover can never escape the app content area and paint over the sidebar
   * (see the `<section>` stacking context in page.tsx that this relies on). */
  const portalRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const source = examSource ?? '';
    setStore(source ? encodeExamDocument(source) : null);
    setBaseline(source);
    setSvg('');
    setLayout(parseLayout((exam as any)?.examLayoutJson));
    setResult(null);
    setActiveId(null);
    setPendingSave(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the source only, see ExamEditor's own header note on this pattern
  }, [examSource]);

  useEffect(() => {
    let cancelled = false;
    if (!examId || !examSource) return undefined;
    getExamSvgQuery(examId)()
      .then(raw => {
        if (cancelled) return;
        const previewResult: ExamSvgResult = JSON.parse(raw);
        if (previewResult.ok && previewResult.svg) setSvg(previewResult.svg);
        // Recomputed from the very same compile as `svg` above — takes over from the mount-time
        // `exam.examLayoutJson` (see its `useEffect` above), which only reflects the LAST actual save and
        // can fall out of sync with this fresh preview the moment the house template's own layout changes
        // without the exam itself having been re-saved (exactly what happens right after a template
        // deploy, for every exam that predates it): the SVG below would then be current while the hitbox
        // overlay still measures the old layout, drifting anywhere the two disagree.
        if (previewResult.ok && previewResult.layout) setLayout(previewResult.layout);
      })
      .catch(() => {
        /* the save/error banner covers a compile failure; a failed preview fetch just leaves the page blank */
      });
    return () => {
      cancelled = true;
    };
  }, [examId, examSource]);

  const [examCreationState, setExamCreationState] = useState<{
    status?: string;
    total?: number;
    finished?: number;
  } | null>(null);

  // Polled on a plain interval rather than via `streamExamStateByExamIdSubscription` — that subscription is
  // documented elsewhere (chat.tsx, chat-input.tsx) as carrying a client-side caching bug in @adornis/baseql
  // that can freeze it on its first-ever value after a resubscribe. This view resubscribes on every canvas
  // open/close, the worst case for that bug, so it uses the same plain-query workaround chat-input.tsx's
  // sidebar progress ring already relies on for this exact field.
  useEffect(() => {
    if (!examId) {
      setExamCreationState(null);
      return undefined;
    }

    let cancelled = false;
    const poll = () => {
      void getExamStateByExamIdQuery(examId)({
        examCreationState: { status: 1, total: 1, finished: 1 },
      } as any)
        .then(freshExamState => {
          if (cancelled) return;
          setExamCreationState((freshExamState as any)?.examCreationState ?? null);
        })
        .catch(err => {
          logger.error(err, 'Failed to poll exam-creation progress');
        });
    };

    poll();
    const intervalId = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [examId]);

  const isGeneratingExam = examCreationState?.status === 'STARTED' || examCreationState?.status === 'RUNNING';
  // The job never touches `examSource` until it finishes (both a fresh create and a refinement patch write
  // it only at the very end — see exam-creation.job.ts) — so whichever value this had the moment generation
  // started stays put for the whole run: empty means nothing existed yet (first generation), non-empty means
  // an existing exam is being patched (refine). Same signal the job's own `ready()` gate uses server-side
  // (`conditions.hasTasks`), read here from the one field this view already tracks.
  const isFirstExamGeneration = !examSource || !examSource.trim();

  const currentSource = useMemo(() => (store ? decodeExamDocument(store) : ''), [store]);
  const isDirty = currentSource !== baseline;

  const triggerSave = useCallback(() => {
    if (isReadOnly) return;
    setPendingSave(true);
  }, [isReadOnly]);

  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    const pdfFileId = (exam as any)?.examSourcePdfId as string | undefined;
    if (!pdfFileId || downloading) return;
    setDownloading(true);
    try {
      const file = await getFileByIDQuery(pdfFileId)({ _id: 1, meta: { fileName: 1, extension: 1 } });
      const downloadUrl = file.getDownloadLink();
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        window.location.href = downloadUrl;
      } else {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${exam?.title ?? 'exam'}.pdf`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const executeSave = useCallback(
    async (sourceToSave: string) => {
      if (!examId || !sourceToSave || saving || isReadOnly) return;
      setSaving(true);
      setResult(null);
      try {
        const raw = await compileExamMutation(examId, sourceToSave)();
        const saveResult: ExamEditorSaveResult = JSON.parse(raw);
        setResult(saveResult);
        if (saveResult.ok) {
          setBaseline(sourceToSave);
          if (saveResult.svg) setSvg(saveResult.svg);
          if (saveResult.layout) setLayout(saveResult.layout);
        }
      } catch (err) {
        setResult({ ok: false, message: err instanceof Error ? err.message : String(err) });
      } finally {
        setSaving(false);
      }
    },
    [examId, saving, isReadOnly],
  );

  useEffect(() => {
    if (pendingSave && !saving) {
      setPendingSave(false);
      executeSave(currentSource);
    }
  }, [pendingSave, saving, currentSource, executeSave]);

  const [refining, setRefining] = useState(false);

  const requestAiChange = useCallback(
    async (instruction: string) => {
      if (!examId || refining || isReadOnly) return;
      setRefining(true);
      setResult(null);
      try {
        const raw = await requestExamRefinementMutation(examId, instruction)();
        const saveResult: ExamEditorSaveResult = JSON.parse(raw);
        setResult(saveResult);
        if (saveResult.ok) {
          if (saveResult.source) {
            setStore(encodeExamDocument(saveResult.source));
            setBaseline(saveResult.source);
          }
          if (saveResult.svg) setSvg(saveResult.svg);
          if (saveResult.layout) setLayout(saveResult.layout);
          setActiveId(null);
        }
      } catch (err) {
        setResult({ ok: false, message: err instanceof Error ? err.message : String(err) });
      } finally {
        setRefining(false);
      }
    },
    [examId, refining, isReadOnly],
  );

  const patchArgs = useCallback((index: number, patch: Record<string, string | null>) => {
    setStore(previous => {
      if (!previous) return previous;
      const node = previous[index];
      if (!node || node.type !== 'call') return previous;
      let next = node;
      for (const [key, value] of Object.entries(patch)) next = setArg(next, key, value);
      return spliceNode(previous, index, next);
    });
  }, []);

  const patchBody = useCallback((index: number, body: string) => {
    setStore(previous => {
      if (!previous) return previous;
      const node = previous[index];
      if (!node || node.type !== 'call') return previous;
      return spliceNode(previous, index, setBody(node, body));
    });
  }, []);

  const patchPositional = useCallback((index: number, rawValue: string) => {
    setStore(previous => {
      if (!previous) return previous;
      const node = previous[index];
      if (!node || node.type !== 'call') return previous;
      return spliceNode(previous, index, setPositionalArg(node, rawValue));
    });
  }, []);

  const patchTypeAndBody = useCallback((index: number, rawType: string, body: string) => {
    setStore(previous => {
      if (!previous) return previous;
      const node = previous[index];
      if (!node || node.type !== 'call') return previous;
      return spliceNode(previous, index, setBody(setArg(node, 'type', rawType), body));
    });
  }, []);

  const deleteNode = useCallback((index: number) => {
    setStore(previous => (previous ? spliceNode(previous, index, null) : previous));
    setActiveId(null);
  }, []);

  const idNodes = useMemo(() => {
    const map = new Map<string, { node: ExamDocumentCallNode; index: number }>();
    if (!store) return map;
    store.forEach((node, index) => {
      if (node.type === 'call' && (node.name === 'exam.with' || node.name === 'exam')) {
        map.set('exam-header', { node, index });
        return;
      }
      if (node.type !== 'call' || (node.name !== 'task' && node.name !== 'material')) return;
      const id = parseTypstString(getArg(node, 'id') ?? '') || '';
      if (id) map.set(id, { node, index });
    });
    return map;
  }, [store]);

  const { pageWidthPt, pageHeightPt, pageCount } = useMemo(() => {
    const fallbackWidth = layout?.pageWidthPt ?? 595.28;
    const fallbackHeight = layout?.pageHeightPt ?? 841.89;

    if (!svg) return { pageWidthPt: fallbackWidth, pageHeightPt: fallbackHeight, pageCount: 1 };

    try {
      const div = document.createElement('div');
      div.innerHTML = svg;
      const rootSvgs = Array.from(div.children).filter(el => el.tagName.toLowerCase() === 'svg');

      if (rootSvgs.length === 1) {
        const viewBox = rootSvgs[0]?.getAttribute('viewBox');
        if (viewBox) {
          const parts = viewBox.split(/[ ,]+/);
          if (parts.length >= 4) {
            const rootHeight = parseFloat(parts[3] ?? '0');
            const count = Math.max(1, Math.round(rootHeight / fallbackHeight));
            return { pageWidthPt: fallbackWidth, pageHeightPt: fallbackHeight, pageCount: count };
          }
        }
      } else if (rootSvgs.length > 1) {
        return { pageWidthPt: fallbackWidth, pageHeightPt: fallbackHeight, pageCount: rootSvgs.length };
      }
    } catch (e) {
      // Ignore DOM errors and fallback quietly
    }

    let maxPage = 1;
    if (layout?.boxes) {
      const pages = Object.values(layout.boxes).map(b => b.page || 1);
      if (pages.length > 0) maxPage = Math.max(...pages);
    }

    return {
      pageWidthPt: fallbackWidth,
      pageHeightPt: fallbackHeight,
      pageCount: maxPage,
    };
  }, [svg, layout]);

  const gaps = useMemo(() => {
    if (!layout) return [];
    type BlockEntry = { id: string; node: ExamDocumentCallNode; box: BlockBoundingBox };
    const blocks = [...idNodes.entries()]
      .map(([id, { node }]) => ({ id, node, box: layout.boxes[id] }))
      .filter((entry): entry is BlockEntry => Boolean(entry.box))
      .sort((a, b) => a.box.page - b.box.page || a.box.y - b.box.y);
    if (blocks.length === 0) return [];

    // A dotted number ("2.a") is a subtask — the template renders it indented under its parent regardless
    // of whether the source actually nests the #task call inside the parent's body or writes it as a flat
    // sibling, so a dotted number is the one signal that works either way. Group every subtask into the
    // MAIN task/material right before it: the "+" only ever offers a seam between two main blocks, but the
    // seam's own position still has to clear every trailing subtask, not sit right after the main task's
    // own header and overlap them.
    const isMain = (entry: BlockEntry) =>
      entry.node.name !== 'task' || !parseTypstString(getArg(entry.node, 'number') ?? '').includes('.');

    const clusters: { main: BlockEntry; last: BlockEntry }[] = [];
    for (const entry of blocks) {
      if (isMain(entry) || clusters.length === 0) {
        clusters.push({ main: entry, last: entry });
      } else {
        clusters[clusters.length - 1]!.last = entry;
      }
    }

    const totalHeightPt = pageHeightPt * pageCount;
    const percentAt = (page: number, y: number) => ((Math.max(0, page - 1) * pageHeightPt + y) / totalHeightPt) * 100;

    const list: { gapId: string; topPercent: number; positionLabel: string }[] = [];
    const first = clusters[0]!.main;
    list.push({
      gapId: 'gap-start',
      topPercent: percentAt(first.box.page, Math.max(0, first.box.y - 10)),
      positionLabel: `before ${describeBlockForAi(first.id, first.node)}`,
    });
    for (let i = 0; i < clusters.length - 1; i++) {
      const a = clusters[i]!.last;
      const b = clusters[i + 1]!.main;
      const bottomA = a.box.y + a.box.height;
      const topPercent =
        a.box.page === b.box.page
          ? percentAt(a.box.page, (bottomA + b.box.y) / 2)
          : percentAt(a.box.page, bottomA + 10);
      list.push({
        gapId: `gap-${a.id}-${b.id}`,
        topPercent,
        positionLabel: `between ${describeBlockForAi(clusters[i]!.main.id, clusters[i]!.main.node)} and ${describeBlockForAi(b.id, b.node)}`,
      });
    }
    const last = clusters[clusters.length - 1]!.last;
    const lastMain = clusters[clusters.length - 1]!.main;
    list.push({
      gapId: 'gap-end',
      topPercent: percentAt(last.box.page, last.box.y + last.box.height + 10),
      positionLabel: `after ${describeBlockForAi(lastMain.id, lastMain.node)}`,
    });
    return list;
  }, [idNodes, layout, pageHeightPt, pageCount]);

  if (!exam) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {t('Canvas.examEditor.noExam')}
      </div>
    );
  }

  const hasPreview = Boolean(store && svg && layout && pageWidthPt > 0 && pageHeightPt > 0);

  return (
    <>
      {/*
       * NO `h-full`, NO `overflow-y-auto` here — ChatCanvas's OWN scroller (chat-canvas.tsx) is the only
       * scrolling frame this view needs, and it already wraps this whole thing. A second, nested
       * `overflow-y-auto` used to sit here too, each with its own `sticky top-0` bar; verified against a real
       * Chromium layout that the combination is what broke it, for two independent reasons:
       *  1. Two real scrollers stacked let a scroll gesture "chain" from this one into ChatCanvas's once this
       *     box's own content bottomed out, sliding this whole block (sticky bar included) down as one unit —
       *     opening a gap between ChatCanvas's real header (a flex sibling, never scrolls) and this view's
       *     own sticky bar, backfilled by the exam preview scrolled into view behind it.
       *  2. Even bounding that with `overscroll-contain` wasn't enough on its own: `h-full` pins this box's
       *     OWN layout height to its parent's, and `overflow: visible` (the default, once overflow-y-auto is
       *     dropped) doesn't grow a box past an explicit height to fit its content — content just paints past
       *     the box's edges. `position: sticky` tracks its element's LAYOUT box, not that visual overflow, so
       *     the sticky bar span was still bounded by the too-short `h-full` box and detached mid-scroll.
       * Dropping both lets this box grow to its real content height, so ChatCanvas's scroller sees the whole
       * thing directly and the sticky bar's range spans it correctly, with nothing left to chain between.
       *
       * `px-3 pb-3`, no top padding: ChatCanvas's own content wrapper (chat-canvas.tsx's
       * CANVAS_CONTENT_PADDING) already pads every canvas view's top edge — stacking this view's own top
       * padding on top of that was measured (real Chromium layout) to triple the gap under the canvas
       * header: 10px (ChatCanvas's) + 12px (this box's own top padding, now removed) + 12px (a `gap-3` slot
       * `portalRootRef` used to sit in as a flex child, now moved outside this flex container entirely
       * instead — see below) = 34px, next to the 10px this leaves alone.
       *
       * `portalRootRef` moves outside this flex container rather than going `position: absolute` in place:
       * floating-ui's popovers portal INTO it, and giving that div its own non-static position would make IT
       * the containing block those popovers resolve against instead of ChatCanvas's own `.relative`
       * scroller — needless risk to positioning already tuned carefully (see POPOVER_MIDDLEWARE).
       */}
      <div ref={portalRootRef} />
      <div className="relative flex flex-col gap-1 px-1 pb-1">
        {/* Covers the sticky save-status bar too, not just the preview — while a background generation/refine
         * job is writing this exam, neither "unsaved changes" nor the PDF download reflect anything real yet
         * (a fresh generation has no PDF at all; a refine's old PDF is about to be replaced). `pointer-events-auto`
         * makes this the thing blocking interaction with the hitboxes/buttons underneath, not just a status
         * indicator over them.
         *
         * THE SPINNER ITSELF IS A NESTED `sticky top-0` BOX, not centered via `inset-0` on this backdrop
         * directly: this backdrop spans the FULL scrollable content (every page, so refining a 3-page exam
         * still blocks clicks on page 1 and 3, not just the page the backdrop happens to cover), but a plain
         * `items-center` centers within THAT full height — landing on whichever page is physically in the
         * middle and leaving the spinner invisible once scrolled elsewhere (or pinned at the very top for a
         * fresh generation, which has no page yet and so barely any height at all). A `sticky top-0` box re-
         * anchors its top edge to the CURRENT scrollport's top edge as ChatCanvas's own scroller moves, and
         * `h-dvh` (taller than any realistic scrollport, on purpose — `overflow` stays visible so it isn't
         * clipped) gives it enough room to flex-center its own content near the middle of whatever is
         * actually on screen right now, at any scroll position, for either case. */}
        {isGeneratingExam && (
          <div className="pointer-events-auto absolute inset-0 z-20 bg-white/70 dark:bg-slate-900/70">
            <div className="sticky top-0 flex h-dvh flex-col items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <Loader2 size={20} className="animate-spin" />
              <span>
                {isFirstExamGeneration ? t('Canvas.examEditor.generatingExam') : t('Canvas.examEditor.refiningExam')}
              </span>
            </div>
          </div>
        )}
        <div className="sticky top-0 z-10 flex shrink-0 flex-col gap-1.5 bg-background/95 py-1 backdrop-blur">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {saving
                ? t('Canvas.examEditor.savingAndRecompiling')
                : isDirty
                  ? t('Canvas.examEditor.unsavedChanges')
                  : t('Canvas.examEditor.allChangesSaved')}
            </span>
            <div className="flex items-center gap-2">
              {/* Opens the online, answerable version of THIS exam (client/app/student-exam/page.tsx). This
                  canvas is shared unconditionally between a teacher's class exam and a student's own
                  self-practice one (see chat.tsx's renderCanvasView — 'examcreator' has no role branch,
                  unlike neighboring cases), and for a student this button is the ONLY way to reach the
                  online fill-in page — there is no other entry point anywhere in the app. Shown only for a
                  student, not a teacher: online submission is now gated to student accounts server-side
                  (resolveIsCurrentSessionStudent in student-exam.ts), so a teacher clicking it would only
                  ever land on a "students only" notice. New tab rather than a route change: the editor is a
                  canvas view inside the chat shell, and navigating away from it would tear down the whole
                  conversation the student is working in. */}
              {role === 'student' && (
                <button
                  onClick={() => window.open(`/student-exam/${examId}`, '_blank', 'noopener')}
                  disabled={!examId}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40"
                >
                  {t('Canvas.examEditor.studentView', { defaultValue: 'Online ausfüllen' })}
                </button>
              )}
              <button
                onClick={handleDownloadPdf}
                disabled={saving || downloading || !(exam as any)?.examSourcePdfId}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40"
              >
                {downloading ? t('Canvas.examEditor.preparingPdf') : t('Canvas.examEditor.downloadPdf')}
              </button>
            </div>
          </div>

          {isReadOnly && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
              {t('Canvas.examEditor.readOnlyBanner')}
            </div>
          )}
        </div>

        {result &&
          (result.ok ? null : (
            <div
              className={`shrink-0 rounded-md border px-2 py-1.5 text-xs ${result.ok ? 'border-emerald-300 text-emerald-700' : 'border-red-300 text-red-700'}`}
            >
              {t('Canvas.examEditor.notSaved', {
                message: result.message ?? t('Canvas.examEditor.seeProblemsBelow'),
              })}
              {result.problems && result.problems.length > 0 && (
                <ul className="mt-1 list-disc pl-4">
                  {result.problems.map((problem, index) => (
                    <li key={index}>
                      {problem.taskNumber ? `${problem.taskNumber}: ` : ''}
                      {problem.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

        {!store && <div className="text-sm text-muted-foreground">{t('Canvas.examEditor.noSourceYet')}</div>}
        {store && !hasPreview && (
          <div className="text-sm text-muted-foreground">{t('Canvas.examEditor.noPreviewYet')}</div>
        )}

        {hasPreview && (
          /* `[transform:translateZ(0)]` is load-bearing, not decorative: `compiler.svg()` (compile-exam.ts) is
           * Typst's "rich" SVG, which embeds an invisible `.tsel` text-selection layer per line of text so the
           * rendered page stays selectable/copyable. That layer's own CSS hardcodes `position: fixed; width:
           * 100%; height: 100%`, meant to be confined by a `transform` Typst's browser runtime would normally
           * put on a wrapper — a runtime this app never loads (only the node compiler is a dependency). Without
           * it, a CSS `transform` PROPERTY is the only thing that creates a containing block for a `fixed`
           * descendant (the `scale(...)` around each glyph run in the SVG is an SVG `transform` ATTRIBUTE, which
           * does not count), so every `.tsel` div resolves against the real viewport instead of this card —
           * full-screen invisible text overlapping everything else on the page, including the chat pane, no
           * matter the canvas width. A no-op transform here is enough to confine them to this card instead.
           */
          <div className="relative mx-auto w-full max-w-3xl rounded-md border border-border bg-white shadow-sm [transform:translateZ(0)]">
            <div
              className="pointer-events-none flex flex-col [&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
              dangerouslySetInnerHTML={{ __html: svg }}
            />
            <div className="absolute inset-0">
              {[...idNodes.entries()].map(([id, { node, index }]) => {
                const box = layout!.boxes[id];
                if (!box) return null;
                return (
                  <Hitbox
                    key={id}
                    id={id}
                    box={box}
                    layout={layout!}
                    pageWidthPt={pageWidthPt}
                    pageHeightPt={pageHeightPt}
                    pageCount={pageCount}
                    node={node}
                    isActive={activeId === id}
                    onSetActiveId={setActiveId}
                    activeId={activeId}
                    onPatchArgs={patch => patchArgs(index, patch)}
                    onPatchPositional={rawValue => patchPositional(index, rawValue)}
                    onPatchBody={body => patchBody(index, body)}
                    onPatchTypeAndBody={(rawType, body) => patchTypeAndBody(index, rawType, body)}
                    onDelete={() => deleteNode(index)}
                    figureAssets={figureAssets}
                    onTriggerSave={triggerSave}
                    onRequestAiChange={requestAiChange}
                    aiChangePending={refining}
                    isReadOnly={isReadOnly}
                    portalRoot={portalRootRef}
                  />
                );
              })}
              {/* No "+" to add a task at all when read-only — there is nowhere for it to lead. */}
              {!isReadOnly &&
                gaps.map(gap => (
                  <AddTaskGap
                    key={gap.gapId}
                    gapId={gap.gapId}
                    topPercent={gap.topPercent}
                    positionLabel={gap.positionLabel}
                    isActive={activeId === gap.gapId}
                    onSetActiveId={setActiveId}
                    onRequestAiChange={requestAiChange}
                    aiChangePending={refining}
                    portalRoot={portalRootRef}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── hitbox + popover (recurses into subtasks/mc/figure-block) ────────────────────────────────────────

/**
 * SHARED BY EVERY POPOVER IN THIS VIEW, and none of these three are the defaults, because the popovers
 * portal INSIDE the canvas (see `portalRootRef`) — anything overflowing the panel is now CLIPPED rather than
 * painted over the app, so nothing may ever leave it.
 *
 * `fallbackPlacements` exists because `flip()` would otherwise only ever consider left/right: for an aligned
 * placement it expands `right-start` to `[right-start, right-end, left-start, left-end]`, and the option
 * that would add the perpendicular axis (`fallbackAxisSideDirection`) defaults to `'none'`. A hitbox spans
 * nearly the full width of the page preview, so in a narrow canvas NO left/right candidate fits and the
 * popover has to go below (or above) the task instead — which is also where it stops covering the very
 * points the teacher is editing.
 *
 * `crossAxis: true` is then only a last resort, for a canvas so narrow (min width is 300px) that even
 * bottom/top overflows: plain `shift()` cannot help a left/right placement at all, since for those its main
 * axis is y and it only ever clamps vertically. Deliberately WITHOUT `limitShift()` — AddTaskGap's reference
 * is a 20px "+" button, and a limiter would clamp the slide to roughly that width, leaving it overflowing
 * again. `size()` caps whatever is left against the clipping ancestors, paired with `overflow-y-auto` on the
 * popover so the cap scrolls instead of cutting content off.
 */
const POPOVER_MIDDLEWARE = [
  offset(8),
  flip({ fallbackPlacements: ['left-start', 'bottom-start', 'top-start'], padding: 8 }),
  shift({ padding: 8, crossAxis: true }),
  size({
    padding: 8,
    apply({ availableWidth, availableHeight, elements }) {
      Object.assign(elements.floating.style, {
        maxWidth: `${availableWidth}px`,
        maxHeight: `${availableHeight}px`,
      });
    },
  }),
];

function Hitbox({
  id,
  box,
  layout,
  pageWidthPt,
  pageHeightPt,
  pageCount,
  node,
  isActive,
  onSetActiveId,
  activeId,
  onPatchArgs,
  onPatchBody,
  onPatchTypeAndBody,
  onDelete,
  figureAssets,
  onTriggerSave,
  onRequestAiChange,
  aiChangePending,
  isReadOnly,
  portalRoot,
}: {
  id: string;
  box: BlockBoundingBox;
  layout: ExamLayout;
  pageWidthPt: number;
  pageHeightPt: number;
  pageCount: number;
  node: ExamDocumentCallNode;
  isActive: boolean;
  onSetActiveId: (id: string | null) => void;
  activeId: string | null;
  figureAssets: FigureAsset[];
  onTriggerSave: () => void;
  /** Dispatches the refinement job with a fully composed instruction — see `describeBlockForAi`. */
  onRequestAiChange: (instruction: string) => void;
  /** True while ANY block's AI request is in flight — at most one popover is open at a time, so this only
   * ever gates the one currently showing it. */
  aiChangePending: boolean;
  /** No popover opens at all — the compiled preview underneath is already the read view. See ExamEditor's own prop doc. */
  isReadOnly?: boolean;
  portalRoot: React.RefObject<HTMLDivElement | null>;
} & NodeWriteBack) {
  const { t } = useTranslation();
  const [hovering, setHovering] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');

  const handleSend = useCallback(() => {
    const comment = aiInstruction.trim();
    if (comment) {
      onRequestAiChange(`The teacher selected ${describeBlockForAi(id, node)} and left this comment: ${comment}`);
      setAiInstruction('');
      return;
    }
    onSetActiveId(null);
    onTriggerSave();
  }, [aiInstruction, onRequestAiChange, id, node, onSetActiveId, onTriggerSave]);

  const handleDelete = useCallback(() => {
    onDelete();
    onTriggerSave();
    onSetActiveId(null);
  }, [onDelete, onTriggerSave, onSetActiveId]);

  const body = node.name === 'task' || node.name === 'material' ? (getBody(node) ?? '') : '';
  const nested = useMemo(() => encodeExamDocument(body, NESTED_TASK_CALLS), [body]);

  const headerBox = useMemo(() => {
    let earliestY: number | null = null;
    for (const child of nested) {
      // Only a subtask/figure-block gets its OWN separate Hitbox rendered below (see the recursion into
      // `nested` further down) — that's the overlap this box exists to avoid. An `#mc` has a tracked box
      // too (so the editor can anchor its options grid), but it is edited through THIS SAME task's popover
      // (McForm, inside TaskForm), not a hitbox of its own — clipping the header there just cuts the
      // clickable area off before the options a teacher is trying to click.
      if (child.type !== 'call' || child.name === 'mc') continue;
      const resolvedId = parseTypstString(getArg(child, 'id') ?? '');
      const childBox = resolvedId ? layout.boxes[resolvedId] : undefined;
      if (!childBox || childBox.page !== box.page) continue;
      if (earliestY === null || childBox.y < earliestY) earliestY = childBox.y;
    }
    if (earliestY === null) return box;
    return { ...box, height: Math.max(0, earliestY - box.y) };
  }, [nested, layout, box]);

  const ownText = useMemo(() => getOwnText(nested), [nested]);
  const patchOwnText = useCallback(
    (text: string) => onPatchBody(decodeExamDocument(setOwnText(nested, text))),
    [nested, onPatchBody],
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isActive,
    onOpenChange: open => {
      if (!open) {
        onSetActiveId(null);
      }
    },
    placement: 'right-start',
    middleware: POPOVER_MIDDLEWARE,
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const childWriteBack = (childIndex: number): NodeWriteBack => ({
    onPatchArgs: patch => {
      const child = nested[childIndex];
      if (!child || child.type !== 'call') return;
      let next = child;
      for (const [key, value] of Object.entries(patch)) next = setArg(next, key, value);
      onPatchBody(decodeExamDocument(spliceNode(nested, childIndex, next)));
    },
    onPatchPositional: rawValue => {
      const child = nested[childIndex];
      if (!child || child.type !== 'call') return;
      onPatchBody(decodeExamDocument(spliceNode(nested, childIndex, setPositionalArg(child, rawValue))));
    },
    onPatchBody: childBody => {
      const child = nested[childIndex];
      if (!child || child.type !== 'call') return;
      onPatchBody(decodeExamDocument(spliceNode(nested, childIndex, setBody(child, childBody))));
    },
    onPatchTypeAndBody: (rawType, body) => {
      const child = nested[childIndex];
      if (!child || child.type !== 'call') return;
      onPatchBody(decodeExamDocument(spliceNode(nested, childIndex, setBody(setArg(child, 'type', rawType), body))));
    },
    onDelete: () => onPatchBody(decodeExamDocument(spliceNode(nested, childIndex, null))),
  });

  const taskTypeValue = node.name === 'task' ? parseTypstString(getArg(node, 'type') ?? '') : '';
  const isLabelImage = taskTypeValue === TaskType.LABEL_IMAGE;

  const mcIndex = nested.findIndex(n => n.type === 'call' && n.name === 'mc');
  const mcNode = mcIndex >= 0 ? (nested[mcIndex] as ExamDocumentCallNode) : null;

  /**
   * A task's `type` is pure metadata (exam.typ's `task` never branches on it — see its own comment), but a
   * `#mc` node in the body is what actually makes typst render an interactive-looking options grid,
   * independently of `type`. Left alone across a type change, the two drift: retyping away from Multiple
   * Choice would keep showing that grid under a type that now says otherwise, and retyping TO it would show
   * the new type label with no options editor, since McForm only ever appears once a `#mc` node exists (see
   * `mcNode` above). This keeps the two in lockstep by folding the `#mc` node's options into (respectively
   * out of) the body in the SAME dispatch as the type change — see `NodeWriteBack.onPatchTypeAndBody`'s own
   * doc comment for why that has to be one atomic write rather than an `onPatchArgs` + `onPatchBody` pair.
   */
  const handleTypeChange = useCallback(
    (nextType: TaskType) => {
      const wasMc = mcNode !== null;
      const willBeMc = nextType === TaskType.MULTIPLE_CHOICE;

      if (wasMc && !willBeMc) {
        const options = parseMcOptions(getPositionalArg(mcNode) ?? '()');
        const optionLines = options.map((option, index) => `${String.fromCharCode(65 + index)}) ${option}`).join('\n');
        const currentText = getOwnText(nested);
        const mergedText = optionLines ? (currentText ? `${currentText}\n\n${optionLines}` : optionLines) : currentText;
        const withoutMc = nested.filter((_, index) => index !== mcIndex);
        onPatchTypeAndBody(formatTypstString(nextType), decodeExamDocument(setOwnText(withoutMc, mergedText)));
        return;
      }

      if (!wasMc && willBeMc) {
        // Restore options from a trailing "A) …\nB) …" block this SAME conversion left behind last time this
        // task left Multiple Choice (see `extractTrailingLetteredOptions`) — rather than always starting from
        // empty, which is what made switching MC → other type → MC lose the original options instead of
        // round-tripping them.
        const currentText = getOwnText(nested);
        const restored = extractTrailingLetteredOptions(currentText);

        // No `id:` — exam-document.ts notes it's optional on `#mc`, existing only so the editor can track a
        // bounding box for the options grid itself; this one is edited through THIS task's own popover
        // (McForm below), never through a hitbox of its own, so it needs no id to be fully editable.
        const mcCallNode: ExamDocumentCallNode = {
          type: 'call',
          name: 'mc',
          headSource: `#mc(${formatMcOptions(restored?.options ?? [])})`,
          args: [],
          body: null,
        };
        const baseNested = restored ? setOwnText(nested, restored.remainingText) : nested;
        onPatchTypeAndBody(formatTypstString(nextType), decodeExamDocument([...baseNested, mcCallNode]));
        return;
      }

      onPatchArgs({ type: formatTypstString(nextType) });
    },
    [mcNode, mcIndex, nested, onPatchTypeAndBody, onPatchArgs],
  );

  return (
    <>
      <div
        ref={refs.setReference}
        style={boxStyle(headerBox, pageWidthPt, pageHeightPt, pageCount)}
        className={`absolute ${isReadOnly ? 'cursor-default' : 'cursor-pointer'}`}
        onMouseEnter={isReadOnly ? undefined : () => setHovering(true)}
        onMouseLeave={isReadOnly ? undefined : () => setHovering(false)}
        onClick={
          isReadOnly
            ? undefined
            : event => {
                event.stopPropagation();
                onSetActiveId(id);
              }
        }
      >
        <div
          className={`h-full w-full rounded-sm border-2 ${
            !isReadOnly && (hovering || isActive)
              ? 'border-dashed border-blue-500/70 bg-blue-500/5'
              : 'border-transparent'
          }`}
        />
      </div>

      {nested.map((child, childIndex) => {
        if (child.type !== 'call') return null;

        if (child.name === 'layout-grid') {
          const gridBody = getBody(child) ?? '';
          const gridNested = encodeExamDocument(gridBody, NESTED_TASK_CALLS);

          return gridNested.map((gridChild, gridChildIndex) => {
            if (gridChild.type !== 'call') return null;
            const resolvedGridId = parseTypstString(getArg(gridChild, 'id') ?? '');
            const gridChildBox = resolvedGridId ? layout.boxes[resolvedGridId] : undefined;
            if (!gridChildBox) return null;

            const gridChildWriteBack = {
              onPatchArgs: (patch: Record<string, string | null>) => {
                let next = gridChild;
                for (const [key, value] of Object.entries(patch)) next = setArg(next, key, value);
                childWriteBack(childIndex).onPatchBody(
                  decodeExamDocument(spliceNode(gridNested, gridChildIndex, next)),
                );
              },
              onPatchPositional: (rawValue: string) => {
                childWriteBack(childIndex).onPatchBody(
                  decodeExamDocument(spliceNode(gridNested, gridChildIndex, setPositionalArg(gridChild, rawValue))),
                );
              },
              onPatchBody: (childBody: string) => {
                childWriteBack(childIndex).onPatchBody(
                  decodeExamDocument(spliceNode(gridNested, gridChildIndex, setBody(gridChild, childBody))),
                );
              },
              onPatchTypeAndBody: (rawType: string, body: string) => {
                childWriteBack(childIndex).onPatchBody(
                  decodeExamDocument(
                    spliceNode(gridNested, gridChildIndex, setBody(setArg(gridChild, 'type', rawType), body)),
                  ),
                );
              },
              onDelete: () => {
                childWriteBack(childIndex).onPatchBody(
                  decodeExamDocument(spliceNode(gridNested, gridChildIndex, null)),
                );
              },
            };

            if (gridChild.name === 'task') {
              return (
                <Hitbox
                  key={resolvedGridId}
                  id={resolvedGridId}
                  box={gridChildBox}
                  layout={layout}
                  pageWidthPt={pageWidthPt}
                  pageHeightPt={pageHeightPt}
                  pageCount={pageCount}
                  node={gridChild}
                  isActive={activeId === resolvedGridId}
                  onSetActiveId={onSetActiveId}
                  activeId={activeId}
                  figureAssets={figureAssets}
                  onTriggerSave={onTriggerSave}
                  onRequestAiChange={onRequestAiChange}
                  aiChangePending={aiChangePending}
                  isReadOnly={isReadOnly}
                  portalRoot={portalRoot}
                  {...gridChildWriteBack}
                />
              );
            }

            if (gridChild.name === 'figure-block') {
              const dotsBox = resolvedGridId ? (layout.boxes[`${resolvedGridId}-image`] ?? gridChildBox) : null;
              return [
                <Hitbox
                  key={`${resolvedGridId}-hitbox`}
                  id={resolvedGridId}
                  box={gridChildBox}
                  layout={layout}
                  pageWidthPt={pageWidthPt}
                  pageHeightPt={pageHeightPt}
                  pageCount={pageCount}
                  node={gridChild}
                  isActive={activeId === resolvedGridId}
                  onSetActiveId={onSetActiveId}
                  activeId={activeId}
                  figureAssets={figureAssets}
                  onTriggerSave={onTriggerSave}
                  onRequestAiChange={onRequestAiChange}
                  aiChangePending={aiChangePending}
                  isReadOnly={isReadOnly}
                  portalRoot={portalRoot}
                  {...gridChildWriteBack}
                />,
                isLabelImage && dotsBox && !isReadOnly ? (
                  <FigureDotsOverlay
                    key={`${resolvedGridId}-dots`}
                    figureBox={dotsBox}
                    pageWidthPt={pageWidthPt}
                    pageHeightPt={pageHeightPt}
                    pageCount={pageCount}
                    callouts={parseCallouts(getArg(gridChild, 'callouts') ?? '()')}
                    onChange={nextCallouts => {
                      gridChildWriteBack.onPatchArgs({ callouts: formatCallouts(nextCallouts) });
                      onTriggerSave();
                    }}
                  />
                ) : null,
              ];
            }
            return null;
          });
        }

        const resolvedId = parseTypstString(getArg(child, 'id') ?? '');
        const childBox = resolvedId ? layout.boxes[resolvedId] : undefined;

        if (child.name === 'task' && childBox) {
          return (
            <Hitbox
              key={resolvedId}
              id={resolvedId}
              box={childBox}
              layout={layout}
              pageWidthPt={pageWidthPt}
              pageHeightPt={pageHeightPt}
              pageCount={pageCount}
              node={child}
              isActive={activeId === resolvedId}
              onSetActiveId={onSetActiveId}
              activeId={activeId}
              figureAssets={figureAssets}
              onTriggerSave={onTriggerSave}
              onRequestAiChange={onRequestAiChange}
              aiChangePending={aiChangePending}
              isReadOnly={isReadOnly}
              portalRoot={portalRoot}
              {...childWriteBack(childIndex)}
            />
          );
        }

        if (child.name === 'figure-block' && childBox) {
          const figureId = resolvedId;
          const dotsBox = figureId ? (layout.boxes[`${figureId}-image`] ?? childBox) : null;

          return [
            <Hitbox
              key={`${resolvedId}-hitbox`}
              id={resolvedId}
              box={childBox}
              layout={layout}
              pageWidthPt={pageWidthPt}
              pageHeightPt={pageHeightPt}
              pageCount={pageCount}
              node={child}
              isActive={activeId === resolvedId}
              onSetActiveId={onSetActiveId}
              activeId={activeId}
              figureAssets={figureAssets}
              onTriggerSave={onTriggerSave}
              onRequestAiChange={onRequestAiChange}
              aiChangePending={aiChangePending}
              isReadOnly={isReadOnly}
              portalRoot={portalRoot}
              {...childWriteBack(childIndex)}
            />,
            isLabelImage && dotsBox && !isReadOnly ? (
              <FigureDotsOverlay
                key={`${resolvedId}-dots`}
                figureBox={dotsBox}
                pageWidthPt={pageWidthPt}
                pageHeightPt={pageHeightPt}
                pageCount={pageCount}
                callouts={parseCallouts(getArg(child, 'callouts') ?? '()')}
                onChange={nextCallouts => {
                  childWriteBack(childIndex).onPatchArgs({ callouts: formatCallouts(nextCallouts) });
                  onTriggerSave();
                }}
              />
            ) : null,
          ];
        }

        return null;
      })}

      {isActive && !isReadOnly && (
        <FloatingPortal root={portalRoot}>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="z-50 w-72 overflow-y-auto rounded-lg border border-border bg-card p-3 shadow-lg"
            >
              {node.name === 'task' ? (
                <TaskForm
                  node={node}
                  onPatchArgs={onPatchArgs}
                  onTypeChange={handleTypeChange}
                  text={ownText}
                  onPatchText={patchOwnText}
                  mcNode={mcNode}
                  onPatchMc={mcNode ? childWriteBack(mcIndex).onPatchPositional : undefined}
                />
              ) : node.name === 'material' ? (
                <MaterialForm node={node} onPatchArgs={onPatchArgs} text={ownText} onPatchText={patchOwnText} />
              ) : node.name === 'figure-block' ? (
                <FigureForm node={node} onPatchArgs={onPatchArgs} />
              ) : node.name === 'exam.with' || node.name === 'exam' ? (
                <HeaderForm node={node} onPatchArgs={onPatchArgs} />
              ) : null}
              <textarea
                value={aiInstruction}
                onChange={e => setAiInstruction(e.target.value)}
                disabled={aiChangePending}
                placeholder={t('Canvas.examEditor.tellAiPlaceholder')}
                rows={2}
                className="mt-3 w-full resize-none rounded-md border border-border bg-transparent p-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
              />
              <div
                className={`mt-2 flex items-center border-t border-border pt-2 ${
                  node.name === 'exam.with' || node.name === 'exam' ? 'justify-end' : 'justify-between'
                }`}
              >
                {node.name !== 'exam.with' && node.name !== 'exam' && (
                  <button
                    onClick={handleDelete}
                    disabled={aiChangePending}
                    className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {t('Canvas.examEditor.delete')}
                  </button>
                )}
                <button
                  onClick={handleSend}
                  disabled={aiChangePending}
                  className="flex items-center gap-1.5 rounded-md bg-brand-strong px-3 py-1 text-xs font-semibold text-white disabled:opacity-70"
                >
                  {aiChangePending && (
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {aiChangePending
                    ? t('Canvas.examEditor.applying')
                    : aiInstruction.trim()
                      ? t('Canvas.examEditor.send')
                      : t('Canvas.examEditor.done')}
                </button>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

// ─── add-task gap (the "+" between top-level blocks) ───────────────────────────────────────────────

/**
 * A THIN HOVER STRIP, not a hitbox on any real content — it sits at the seam between two top-level
 * blocks (or before the first / after the last) with nothing of its own in `layout.boxes`, so its
 * position is derived from its NEIGHBOURS' boxes (see `gaps` in `ExamEditor`) rather than tracked geometry.
 * Reuses the same floating popover + `onRequestAiChange` plumbing as a real `Hitbox`'s "tell AI" box, just
 * with the position baked into the composed instruction instead of a block selection.
 */
function AddTaskGap({
  gapId,
  topPercent,
  positionLabel,
  isActive,
  onSetActiveId,
  onRequestAiChange,
  aiChangePending,
  portalRoot,
}: {
  gapId: string;
  topPercent: number;
  positionLabel: string;
  isActive: boolean;
  onSetActiveId: (id: string | null) => void;
  onRequestAiChange: (instruction: string) => void;
  aiChangePending: boolean;
  portalRoot: React.RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const [instruction, setInstruction] = useState('');

  const { refs, floatingStyles, context } = useFloating({
    open: isActive,
    onOpenChange: open => {
      if (!open) onSetActiveId(null);
    },
    placement: 'right-start',
    middleware: POPOVER_MIDDLEWARE,
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  const handleAdd = useCallback(() => {
    const comment = instruction.trim();
    if (!comment || aiChangePending) return;
    onRequestAiChange(`The teacher clicked + to add a new task ${positionLabel} and described: ${comment}`);
    setInstruction('');
  }, [instruction, aiChangePending, onRequestAiChange, positionLabel]);

  return (
    <div
      className="group pointer-events-none absolute inset-x-0 z-10 flex items-center justify-center"
      style={{ top: `${topPercent}%`, height: '20px', marginTop: '-10px' }}
    >
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={() => onSetActiveId(isActive ? null : gapId)}
        className="pointer-events-auto flex h-5 w-5 items-center justify-center rounded-full border border-border bg-white text-xs font-bold leading-none text-muted-foreground opacity-0 shadow-sm transition-opacity hover:bg-brand-strong hover:text-white group-hover:opacity-100"
        aria-label={`Add a task ${positionLabel}`}
      >
        +
      </button>
      {isActive && (
        <FloatingPortal root={portalRoot}>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="pointer-events-auto z-50 w-72 overflow-y-auto rounded-lg border border-border bg-card p-3 shadow-lg"
            >
              <textarea
                autoFocus
                value={instruction}
                onChange={e => setInstruction(e.target.value)}
                disabled={aiChangePending}
                placeholder={t('Canvas.examEditor.describeAiPlaceholder')}
                rows={3}
                className="w-full resize-none rounded-md border border-border bg-transparent p-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
              />
              <div className="mt-2 flex justify-end border-t border-border pt-2">
                <button
                  onClick={handleAdd}
                  disabled={aiChangePending || !instruction.trim()}
                  className="flex items-center gap-1.5 rounded-md bg-brand-strong px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {aiChangePending && (
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {aiChangePending ? t('Canvas.examEditor.adding') : t('Canvas.examEditor.add')}
                </button>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
}

/** Every selectable type, DEFAULT excluded — same convention as the structure editor's own type picker
 * (structure-panels.tsx): DEFAULT is the implicit "nothing more specific applies" fallback a task's `type`
 * already resolves to when unset, not something to actively re-select from the list. */
const SELECTABLE_TASK_TYPES = (Object.values(TaskType) as TaskType[]).filter(type => type !== TaskType.DEFAULT);

/**
 * A small dropdown, not a native `<select>`, so the option list can carry the same look (rounded card,
 * per-row hover) as every other floating panel in this editor instead of the browser's own `<select>`
 * chrome. It lives inside the task popover, itself already a floating-ui popover with `useDismiss` — the
 * `stopPropagation()` calls on both the trigger and the panel keep opening/choosing from this one from also
 * closing (or reopening) the outer popover.
 */
function TaskTypeSelect({ value, onChange }: { value: string; onChange: (nextType: TaskType) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = getTaskTypeLabel(value as TaskType, t) || value;

  return (
    <div className="relative">
      <button
        type="button"
        onPointerDown={event => event.stopPropagation()}
        onClick={event => {
          event.stopPropagation();
          setOpen(current => !current);
        }}
        className="flex w-full items-center justify-between gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground outline-none focus:border-brand-strong"
      >
        <span className="truncate">{label}</span>
        <span className="text-muted-foreground">▾</span>
      </button>
      {open && (
        <div
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          className="absolute left-0 top-full z-50 mt-1 max-h-60 w-max min-w-full overflow-y-auto overscroll-contain rounded-md border border-border bg-card p-1 shadow-lg"
        >
          {SELECTABLE_TASK_TYPES.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => {
                onChange(type);
                setOpen(false);
              }}
              className={`block w-full rounded px-2 py-1 text-left text-xs ${
                type === value ? 'bg-brand-strong/10 font-semibold text-brand-strong' : 'text-foreground hover:bg-muted'
              }`}
            >
              {getTaskTypeLabel(type, t)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskForm({
  node,
  onPatchArgs,
  onTypeChange,
  text,
  onPatchText,
  mcNode,
  onPatchMc,
}: {
  node: ExamDocumentCallNode;
  onTypeChange: (nextType: TaskType) => void;
  text: string;
  onPatchText: (text: string) => void;
  mcNode?: ExamDocumentCallNode | null;
  onPatchMc?: (rawValue: string) => void;
} & Pick<NodeWriteBack, 'onPatchArgs'>) {
  const { t } = useTranslation();
  const number = parseTypstString(getArg(node, 'number') ?? '');
  const taskType = parseTypstString(getArg(node, 'type') ?? '');
  const points = getArg(node, 'points') ?? '';
  const lines = getArg(node, 'lines') ?? '';

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('Canvas.examEditor.task', { number })}
      </h3>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('Canvas.examEditor.taskNumber')}</span>
          <div className="rounded-md border border-border bg-muted/50 px-2 py-1 font-mono text-xs text-muted-foreground">
            {number}
          </div>
        </div>
        <div className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('Canvas.examEditor.type')}</span>
          <TaskTypeSelect value={taskType} onChange={onTypeChange} />
        </div>
        <TextField
          label={t('Canvas.examEditor.points')}
          value={points}
          onCommit={v => onPatchArgs({ points: v.trim() || null })}
        />
        <TextField
          label={t('Canvas.examEditor.lines')}
          value={lines}
          onCommit={v => onPatchArgs({ lines: v.trim() || null })}
        />
      </div>
      <TextAreaField label={t('Canvas.examEditor.taskText')} value={text} onCommit={onPatchText} />
      {mcNode && onPatchMc && (
        <div className="mt-2 border-t border-border pt-2">
          <McForm node={mcNode} onPatchPositional={onPatchMc} />
        </div>
      )}
    </div>
  );
}

function MaterialForm({
  node,
  onPatchArgs,
  text,
  onPatchText,
}: {
  node: ExamDocumentCallNode;
  text: string;
  onPatchText: (text: string) => void;
} & Pick<NodeWriteBack, 'onPatchArgs'>) {
  const { t } = useTranslation();
  const tasksRaw = getArg(node, 'tasks');
  const tasksDisplay = tasksRaw ? parseTypstStringArray(tasksRaw).join(', ') : '';

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('Canvas.examEditor.material')}
      </h3>
      <TextField
        label={t('Canvas.examEditor.title')}
        value={parseTypstString(getArg(node, 'title') ?? '')}
        onCommit={v => onPatchArgs({ title: v.trim() ? formatTypstString(v) : null })}
      />
      <TextField
        label={t('Canvas.examEditor.usedByTasks')}
        value={tasksDisplay}
        onCommit={v => {
          const numbers = v
            .split(',')
            .map(part => part.trim())
            .filter(Boolean);
          onPatchArgs({ tasks: formatTypstStringArray(numbers) });
        }}
      />
      <TextAreaField label={t('Canvas.examEditor.text')} value={text} onCommit={onPatchText} />
    </div>
  );
}

function McForm({
  node,
  onPatchPositional,
}: { node: ExamDocumentCallNode } & Pick<NodeWriteBack, 'onPatchPositional'>) {
  const { t } = useTranslation();
  const options = useMemo(() => parseMcOptions(getPositionalArg(node) ?? '()'), [node]);
  const [draft, setDraft] = useState(options);
  useEffect(() => setDraft(options), [options]);

  const commit = (next: string[]) => {
    setDraft(next);
    onPatchPositional(formatMcOptions(next.filter(option => option.trim().length > 0)));
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('Canvas.examEditor.multipleChoice')}
      </h3>
      {draft.map((option, index) => (
        <div key={index} className="flex items-center gap-1">
          <span className="w-4 text-xs text-muted-foreground">{String.fromCharCode(65 + index)})</span>
          <input
            value={option}
            onChange={event => {
              const next = draft.slice();
              next[index] = event.target.value;
              setDraft(next);
            }}
            onBlur={() => commit(draft)}
            className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-brand-strong"
          />
          <button
            onClick={() => commit(draft.filter((_, i) => i !== index))}
            className="px-1 text-xs text-muted-foreground hover:text-red-600"
            title={t('Canvas.examEditor.removeOption')}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        onClick={() => commit([...draft, ''])}
        className="rounded-md border border-dashed border-border py-1 text-xs font-medium text-muted-foreground hover:border-brand-strong hover:text-brand-strong"
      >
        {t('Canvas.examEditor.addOption')}
      </button>
    </div>
  );
}

// ─── figure callouts (draggable directly on hover) ─────────────────────────────────────────────────

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function FigureDotsOverlay({
  figureBox,
  pageWidthPt,
  pageHeightPt,
  pageCount,
  callouts,
  onChange,
}: {
  figureBox: BlockBoundingBox;
  pageWidthPt: number;
  pageHeightPt: number;
  pageCount: number;
  callouts: ExamCallout[];
  onChange: (next: ExamCallout[]) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleCommit = (calloutIndex: number, position: { x: number; y: number }) => {
    onChange(callouts.map((callout, i) => (i === calloutIndex ? { ...callout, ...position } : callout)));
  };

  return (
    <div
      ref={containerRef}
      className={`absolute rounded-sm border-2 border-blue-500/70 bg-black/5 transition-opacity ${
        dragging ? 'opacity-100' : 'opacity-0 hover:opacity-100 focus-within:opacity-100'
      }`}
      style={{ ...boxStyle(figureBox, pageWidthPt, pageHeightPt, pageCount), touchAction: 'none' }}
    >
      {callouts.map((callout, calloutIndex) => (
        <CalloutDot
          key={calloutIndex}
          callout={callout}
          index={calloutIndex}
          containerRef={containerRef}
          onCommit={handleCommit}
          onDraggingChange={setDragging}
        />
      ))}
    </div>
  );
}

function CalloutDot({
  callout,
  index,
  containerRef,
  onCommit,
  onDraggingChange,
}: {
  callout: ExamCallout;
  index: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onCommit: (index: number, position: { x: number; y: number }) => void;
  onDraggingChange: (dragging: boolean) => void;
}) {
  const [live, setLive] = useState<{ x: number; y: number } | null>(null);

  const positionFromEvent = (event: React.PointerEvent<HTMLButtonElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clamp01((event.clientX - rect.left) / rect.width),
      y: clamp01((event.clientY - rect.top) / rect.height),
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    onDraggingChange(true);
    setLive({ x: callout.x, y: callout.y });
  };
  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!live) return;
    const next = positionFromEvent(event);
    if (next) setLive(next);
  };
  const handlePointerUp = () => {
    if (live) onCommit(index, live);
    setLive(null);
    onDraggingChange(false);
  };

  const position = live ?? callout;
  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      title={callout.label}
      style={{ left: `${position.x * 100}%`, top: `${position.y * 100}%` }}
      className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white bg-brand-strong shadow active:cursor-grabbing"
    />
  );
}

function FigureForm({
  node,
  onPatchArgs,
}: {
  node: ExamDocumentCallNode;
} & Pick<NodeWriteBack, 'onPatchArgs'>) {
  const { t } = useTranslation();
  const rawCaption = getArg(node, 'caption') ?? '';
  const captionText = rawCaption.startsWith('[') && rawCaption.endsWith(']') ? rawCaption.slice(1, -1) : rawCaption;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('Canvas.examEditor.figureProperties')}
      </h3>
      <TextField
        label={t('Canvas.examEditor.caption')}
        value={captionText}
        onCommit={v => onPatchArgs({ caption: v.trim() ? `[${v.trim()}]` : null })}
      />
    </div>
  );
}

function HeaderForm({
  node,
  onPatchArgs,
}: {
  node: ExamDocumentCallNode;
} & Pick<NodeWriteBack, 'onPatchArgs'>) {
  const { t } = useTranslation();
  const title = parseTypstString(getArg(node, 'title') ?? '');
  const subject = parseTypstString(getArg(node, 'subject') ?? '');
  const classStr = parseTypstString(getArg(node, 'class') ?? '');
  const duration = parseTypstString(getArg(node, 'duration') ?? '');
  const totalPoints = getArg(node, 'total-points') ?? '';

  const rawHints = getArg(node, 'hints') ?? '';
  const hintsText =
    rawHints.startsWith('[') && rawHints.endsWith(']') ? rawHints.slice(1, -1) : parseTypstString(rawHints);

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('Canvas.examEditor.examSetup')}
      </h3>
      <TextField
        label={t('Canvas.examEditor.title')}
        value={title}
        onCommit={v => onPatchArgs({ title: v.trim() ? formatTypstString(v) : null })}
      />
      <div className="grid grid-cols-2 gap-2">
        <TextField
          label={t('Canvas.examEditor.subject')}
          value={subject}
          onCommit={v => onPatchArgs({ subject: v.trim() ? formatTypstString(v) : null })}
        />
        <TextField
          label={t('Canvas.examEditor.class')}
          value={classStr}
          onCommit={v => onPatchArgs({ class: v.trim() ? formatTypstString(v) : null })}
        />
        <TextField
          label={t('Canvas.examEditor.duration')}
          value={duration}
          onCommit={v => onPatchArgs({ duration: v.trim() ? formatTypstString(v) : null })}
        />
        <TextField
          label={t('Canvas.examEditor.totalPoints')}
          value={totalPoints}
          onCommit={v => onPatchArgs({ 'total-points': v.trim() || null })}
        />
      </div>
      <TextAreaField
        label={t('Canvas.examEditor.globalHints')}
        value={hintsText}
        onCommit={v => onPatchArgs({ hints: v.trim() ? `[${v.trim()}]` : null })}
      />
    </div>
  );
}

// ─── field inputs ───────────────────────────────────────────────────────────────────────────────────

function TextField({ label, value, onCommit }: { label: string; value: string; onCommit: (value: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        value={draft}
        onChange={event => setDraft(event.target.value)}
        onBlur={() => draft !== value && onCommit(draft)}
        className="rounded-md border border-border bg-background px-2 py-1 outline-none focus:border-brand-strong"
      />
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <label className="mt-1 flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <textarea
        value={draft}
        onChange={event => setDraft(event.target.value)}
        onBlur={() => draft !== value && onCommit(draft)}
        spellCheck={false}
        rows={Math.min(10, Math.max(3, draft.split('\n').length + 1))}
        className="w-full resize-y rounded-md border border-border bg-background p-2 font-mono text-xs leading-relaxed outline-none focus:border-brand-strong"
      />
    </label>
  );
}
